import type { ReactNode } from "react";
import { FileText, ShieldCheck } from "lucide-react";
import { StepInfoRow } from "../StepInfoRow";
import { getString } from "./helpers";
import type { SummaryTranslator } from "./types";

export function getValidateSummary(config: Record<string, unknown>, t: SummaryTranslator): ReactNode[] {
  const items: ReactNode[] = [];

  const rules = Array.isArray(config.rules)
    ? (config.rules as { field: string; rule: string; pattern?: string; min?: number; max?: number }[])
    : [];
  const onInvalid = getString(config.onInvalid) || "skip";

  const modeLabels: Record<string, { labelKey: string; color: string }> = {
    skip: { labelKey: "summaries.validate.mode_skip", color: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300" },
    fail: { labelKey: "summaries.validate.mode_fail", color: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300" },
    log: { labelKey: "summaries.validate.mode_log", color: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" },
  };
  const modeInfo = modeLabels[onInvalid] || modeLabels.skip;

  items.push(
    <StepInfoRow
      key="mode"
      icon={<ShieldCheck className="size-3.5" />}
      label={t("summaries.validate.label_mode")}
      value={
        <span className="flex items-center gap-2">
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${modeInfo.color}`}>
            {t(modeInfo.labelKey)}
          </span>
          <span className="text-[10px] text-gray-400">{t("summaries.validate.rules_count", { count: rules.length })}</span>
        </span>
      }
    />
  );

  const maxDisplay = 4;
  rules.slice(0, maxDisplay).forEach((r, i) => {
    let detail = r.rule;
    if (r.pattern) detail += `: ${r.pattern}`;
    if (r.min !== undefined) detail += ` >= ${r.min}`;
    if (r.max !== undefined) detail += ` <= ${r.max}`;
    items.push(
      <StepInfoRow key={`rule-${i}`} icon={<FileText className="size-3.5" />} label={r.field} value={detail} mono />
    );
  });

  if (rules.length > maxDisplay) {
    items.push(
      <div key="more" className="pl-6 text-[10px] text-gray-400 dark:text-gray-500">
        {t("summaries.validate.more_rules", { count: rules.length - maxDisplay })}
      </div>
    );
  }

  return items;
}
